import React, { useState } from 'react';
import { Zap, Loader } from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { RootState } from '../../../reducers';
import logger from '../../../lib/logger';
import { fetchSubscription } from '../../plan/thunk';
import { setAlertMessage } from '../../dashboard/dashboardSlice';
import { endTrialImmediately } from '../../../services/subscriptionServices';

const ActivateTrialButton: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { plan, team } = useSelector((state: RootState) => state);
  const { teamId } = team.selectedTeam || {};
  const {
    state: subscriptionState,
    provider,
    package: planPackage,
  } = plan.subscription || {};

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only show for Stripe trial subscriptions
  const isTrialing = subscriptionState === 'trialing' && provider === 'stripe';

  if (!isTrialing) {
    return null;
  }

  const handleActivate = async () => {
    if (!teamId) {
      logger.error('No teamId found');
      return;
    }

    setIsActivating(true);
    setError(null);

    try {
      const subscriptionData = await endTrialImmediately({ teamId });

      if (!subscriptionData) {
        throw new Error('Failed to activate subscription');
      }

      // Refresh subscription data
      await dispatch(fetchSubscription({ teamId }));

      // Close dialog
      setIsDialogOpen(false);

      // Show success message
      dispatch(
        setAlertMessage({
          type: 'success',
          message: t(
            'billing.trial.activateSuccess',
            'Your subscription has been activated! You are now on the paid plan.',
          ),
        }),
      );

      logger.info('Trial activated successfully', { teamId });
    } catch (err: any) {
      const errorMessage =
        err?.message ||
        t(
          'billing.trial.activateError',
          'Failed to activate subscription. Please try again.',
        );
      logger.error('Failed to activate trial', { error: err, teamId });
      setError(errorMessage);

      dispatch(
        setAlertMessage({
          type: 'error',
          message: errorMessage,
        }),
      );
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <>
      <Button
        variant="default"
        size="default"
        onClick={() => setIsDialogOpen(true)}
        className="gap-2"
      >
        <Zap className="h-4 w-4" />
        {t('billing.trial.activateNow', 'Activate Now')}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t(
                'billing.trial.activateTitle',
                'Activate Your Subscription Now?',
              )}
            </DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                {t(
                  'billing.trial.activateDescription',
                  'This will end your trial period immediately and activate your paid subscription. You will be charged now instead of at the end of your trial.',
                )}
              </p>
              <p className="font-medium">
                {t('billing.trial.activatePackage', `Plan: ${planPackage}`)}
              </p>
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isActivating}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handleActivate}
              disabled={isActivating}
              className="gap-2"
            >
              {isActivating ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  {t('billing.trial.activating', 'Activating...')}
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  {t('billing.trial.confirmActivate', 'Yes, Activate Now')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ActivateTrialButton;
